import {
  isProblemScoreStatus,
  type ProblemScoreStatus,
} from "../lib/problem-score-status";

export type ProblemAnswerValue = string | Record<string, string | number>;
export type ProblemAnswerDraftValue = ProblemAnswerValue;

export interface ProblemAnswerSessionState {
  answer: ProblemAnswerValue;
  isCorrectAnswerVisible: boolean;
  isRevealedWithoutGrading: boolean;
  isPartialAnswerReveal?: boolean;
  scoreStatus?: ProblemScoreStatus;
  // 부분점수 정확 비율(0~1). 리사이즈 리마운트 복원 시 점수 카운터를 status 근사(0.5) 대신
  // 정확 비율로 되살리기 위해 저장한다(JCG-2806). correct=1/incorrect=0 은 status 로 충분해 미저장.
  scoreRatio?: number;
}

const STORAGE_KEY_PREFIX = "jcg";
const ENCODED_PATH_SEPARATOR = ":";
const runtimeProblemAnswerSessionStateKeys = new Set<string>();

export const RANDOM_PROBLEMS_KEEP_TTL_MS = 60 * 60 * 1000;
export const RANDOM_PROBLEMS_KEEP_MODE_STORAGE_KEY = `${STORAGE_KEY_PREFIX}:random-problems:keep-mode`;

interface TimedStoragePayload<T> {
  savedAt: number;
  value: T;
}

export interface KeptRandomProblemIdsState {
  problemIds: string[];
  expiresAt: number;
}

function getLocalStorage(): Storage | null {
  if (typeof window === "undefined") return null;

  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

function encodeStoragePart(value: string): string {
  return encodeURIComponent(value);
}

function getTimedStoragePayload(key: string): TimedStoragePayload<unknown> | null {
  const storage = getLocalStorage();
  if (!storage) return null;

  try {
    const stored = storage.getItem(key);
    if (!stored) return null;

    const parsed = JSON.parse(stored) as Partial<TimedStoragePayload<unknown>>;
    if (
      !parsed ||
      typeof parsed !== "object" ||
      typeof parsed.savedAt !== "number" ||
      !("value" in parsed)
    ) {
      storage.removeItem(key);
      return null;
    }

    if (Date.now() - parsed.savedAt > RANDOM_PROBLEMS_KEEP_TTL_MS) {
      storage.removeItem(key);
      return null;
    }

    return {
      savedAt: parsed.savedAt,
      value: parsed.value,
    };
  } catch {
    storage.removeItem(key);
    return null;
  }
}

function setTimedStoragePayload<T>(key: string, value: T): number | null {
  const storage = getLocalStorage();
  if (!storage) return null;

  try {
    const savedAt = Date.now();
    const payload: TimedStoragePayload<T> = {
      savedAt,
      value,
    };
    storage.setItem(key, JSON.stringify(payload));
    return savedAt + RANDOM_PROBLEMS_KEEP_TTL_MS;
  } catch {
    // localStorage may be unavailable in private mode or under quota pressure.
    return null;
  }
}

function removeStorageItem(key: string): void {
  const storage = getLocalStorage();
  if (!storage) return;

  try {
    storage.removeItem(key);
  } catch {
    // Ignore unavailable localStorage.
  }
}

export function getRandomProblemsKeepMode(): boolean {
  const storage = getLocalStorage();
  if (!storage) return false;

  try {
    return storage.getItem(RANDOM_PROBLEMS_KEEP_MODE_STORAGE_KEY) === "true";
  } catch {
    return false;
  }
}

export function saveRandomProblemsKeepMode(enabled: boolean): void {
  const storage = getLocalStorage();
  if (!storage) return;

  try {
    storage.setItem(RANDOM_PROBLEMS_KEEP_MODE_STORAGE_KEY, String(enabled));
  } catch {
    // Ignore unavailable localStorage.
  }
}

export function getKeptRandomProblemIdsStorageKey(pathname: string): string {
  return `${STORAGE_KEY_PREFIX}:random-problems:kept:${encodeStoragePart(pathname)}`;
}

export function getKeptRandomProblemIdsState(pathname: string): KeptRandomProblemIdsState | null {
  const payload = getTimedStoragePayload(getKeptRandomProblemIdsStorageKey(pathname));
  if (!payload || !Array.isArray(payload.value)) return null;

  const problemIds = payload.value.filter((value): value is string => typeof value === "string");
  if (problemIds.length === 0) return null;

  return {
    problemIds,
    expiresAt: payload.savedAt + RANDOM_PROBLEMS_KEEP_TTL_MS,
  };
}

export function getKeptRandomProblemIds(pathname: string): string[] | null {
  return getKeptRandomProblemIdsState(pathname)?.problemIds ?? null;
}

export function getKeptRandomProblemsExpiresAt(pathname: string): number | null {
  return getKeptRandomProblemIdsState(pathname)?.expiresAt ?? null;
}

export function saveKeptRandomProblemIds(pathname: string, problemIds: string[]): number | null {
  if (problemIds.length === 0) return null;
  return setTimedStoragePayload(getKeptRandomProblemIdsStorageKey(pathname), problemIds);
}

export function clearKeptRandomProblemIds(pathname: string): void {
  removeStorageItem(getKeptRandomProblemIdsStorageKey(pathname));
}

function getProblemAnswerDraftStoragePrefix(pathname: string): string {
  return `${STORAGE_KEY_PREFIX}:problem-answer:draft:${encodeStoragePart(pathname)}${ENCODED_PATH_SEPARATOR}`;
}

function getProblemAnswerSessionStateStoragePrefix(pathname: string): string {
  return `${STORAGE_KEY_PREFIX}:problem-answer:session:${encodeStoragePart(pathname)}${ENCODED_PATH_SEPARATOR}`;
}

export function getProblemAnswerDraftStorageKey(pathname: string, problemId: string): string {
  return `${getProblemAnswerDraftStoragePrefix(pathname)}${encodeStoragePart(problemId)}`;
}

export function getProblemAnswerSessionStateStorageKey(
  pathname: string,
  problemId: string
): string {
  return `${getProblemAnswerSessionStateStoragePrefix(pathname)}${encodeStoragePart(problemId)}`;
}

export function hasRuntimeProblemAnswerSessionState(
  pathname: string,
  problemId: string
): boolean {
  return runtimeProblemAnswerSessionStateKeys.has(
    getProblemAnswerSessionStateStorageKey(pathname, problemId)
  );
}

function isProblemAnswerDraftValue(value: unknown): value is ProblemAnswerDraftValue {
  if (typeof value === "string") return true;
  if (!value || typeof value !== "object" || Array.isArray(value)) return false;

  return Object.values(value).every(
    (entry) => typeof entry === "string" || typeof entry === "number"
  );
}

function isProblemAnswerSessionState(value: unknown): value is ProblemAnswerSessionState {
  if (!value || typeof value !== "object" || Array.isArray(value)) return false;

  const candidate = value as Partial<ProblemAnswerSessionState>;
  return (
    isProblemAnswerDraftValue(candidate.answer) &&
    typeof candidate.isCorrectAnswerVisible === "boolean" &&
    typeof candidate.isRevealedWithoutGrading === "boolean" &&
    (
      candidate.scoreStatus === undefined ||
      isProblemScoreStatus(candidate.scoreStatus)
    ) &&
    (
      candidate.scoreRatio === undefined ||
      (typeof candidate.scoreRatio === "number" &&
        Number.isFinite(candidate.scoreRatio))
    )
  );
}

export function getProblemAnswerDraft(
  pathname: string,
  problemId: string
): ProblemAnswerDraftValue | null {
  const key = getProblemAnswerDraftStorageKey(pathname, problemId);
  const payload = getTimedStoragePayload(key);
  if (!payload || !isProblemAnswerDraftValue(payload.value)) return null;
  return payload.value;
}

export function saveProblemAnswerDraft(
  pathname: string,
  problemId: string,
  answer: ProblemAnswerDraftValue
): number | null {
  return setTimedStoragePayload(getProblemAnswerDraftStorageKey(pathname, problemId), answer);
}

export function clearProblemAnswerDraft(pathname: string, problemId: string): void {
  removeStorageItem(getProblemAnswerDraftStorageKey(pathname, problemId));
}

export function getProblemAnswerSessionState(
  pathname: string,
  problemId: string
): ProblemAnswerSessionState | null {
  const key = getProblemAnswerSessionStateStorageKey(pathname, problemId);
  const payload = getTimedStoragePayload(key);
  if (!payload || !isProblemAnswerSessionState(payload.value)) return null;
  return payload.value;
}

export function saveProblemAnswerSessionState(
  pathname: string,
  problemId: string,
  state: ProblemAnswerSessionState
): number | null {
  const key = getProblemAnswerSessionStateStorageKey(pathname, problemId);
  const expiresAt = setTimedStoragePayload(key, state);
  if (expiresAt !== null) {
    runtimeProblemAnswerSessionStateKeys.add(key);
  }
  return expiresAt;
}

export function clearProblemAnswerSessionState(pathname: string, problemId: string): void {
  const key = getProblemAnswerSessionStateStorageKey(pathname, problemId);
  removeStorageItem(key);
  runtimeProblemAnswerSessionStateKeys.delete(key);
}

export function clearProblemAnswerDraftsForPath(
  pathname: string,
  problemIds?: string[]
): void {
  if (problemIds && problemIds.length > 0) {
    problemIds.forEach((problemId) => clearProblemAnswerDraft(pathname, problemId));
    return;
  }

  const storage = getLocalStorage();
  if (!storage) return;

  const prefix = getProblemAnswerDraftStoragePrefix(pathname);
  try {
    for (let index = storage.length - 1; index >= 0; index -= 1) {
      const key = storage.key(index);
      if (key?.startsWith(prefix)) {
        storage.removeItem(key);
      }
    }
  } catch {
    // Ignore unavailable localStorage.
  }
}

export function clearProblemAnswerSessionStatesForPath(
  pathname: string,
  problemIds?: string[]
): void {
  if (problemIds && problemIds.length > 0) {
    problemIds.forEach((problemId) => clearProblemAnswerSessionState(pathname, problemId));
    return;
  }

  const storage = getLocalStorage();
  if (!storage) return;

  const prefix = getProblemAnswerSessionStateStoragePrefix(pathname);
  try {
    for (let index = storage.length - 1; index >= 0; index -= 1) {
      const key = storage.key(index);
      if (key?.startsWith(prefix)) {
        storage.removeItem(key);
        runtimeProblemAnswerSessionStateKeys.delete(key);
      }
    }
  } catch {
    // Ignore unavailable localStorage.
  }
}

export function refreshProblemAnswerDraftsForPath(pathname: string, problemIds?: string[]): void {
  if (problemIds && problemIds.length > 0) {
    problemIds.forEach((problemId) => {
      const draft = getProblemAnswerDraft(pathname, problemId);
      if (draft !== null) {
        saveProblemAnswerDraft(pathname, problemId, draft);
      }
    });
    return;
  }

  const storage = getLocalStorage();
  if (!storage) return;

  const prefix = getProblemAnswerDraftStoragePrefix(pathname);
  try {
    for (let index = storage.length - 1; index >= 0; index -= 1) {
      const key = storage.key(index);
      if (!key?.startsWith(prefix)) continue;

      const payload = getTimedStoragePayload(key);
      if (payload && isProblemAnswerDraftValue(payload.value)) {
        setTimedStoragePayload(key, payload.value);
      }
    }
  } catch {
    // Ignore unavailable localStorage.
  }
}

export function refreshProblemAnswerSessionStatesForPath(
  pathname: string,
  problemIds?: string[]
): void {
  if (problemIds && problemIds.length > 0) {
    problemIds.forEach((problemId) => {
      const state = getProblemAnswerSessionState(pathname, problemId);
      if (state !== null) {
        saveProblemAnswerSessionState(pathname, problemId, state);
      }
    });
    return;
  }

  const storage = getLocalStorage();
  if (!storage) return;

  const prefix = getProblemAnswerSessionStateStoragePrefix(pathname);
  try {
    for (let index = storage.length - 1; index >= 0; index -= 1) {
      const key = storage.key(index);
      if (!key?.startsWith(prefix)) continue;

      const payload = getTimedStoragePayload(key);
      if (payload && isProblemAnswerSessionState(payload.value)) {
        const expiresAt = setTimedStoragePayload(key, payload.value);
        if (expiresAt !== null) {
          runtimeProblemAnswerSessionStateKeys.add(key);
        }
      }
    }
  } catch {
    // Ignore unavailable localStorage.
  }
}
